import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  Box, Button, Container, Grid, Paper, TextField, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Checkbox, FormControlLabel
} from '@mui/material';
import { listClientes, createCliente, updateCliente, getClientesSchema, getCliente } from '../../services/clientes';
import { listServicos } from '../../services/servicos';

// Ordem fixa solicitada para serviços
const SERVICOS_ORDERED = [
  'Implantação Hadritech', 'Implantação TOTVS PMS',
  'Implantação TOTVS POS', 'Implantação TOTVS BackSaaS',
  'Suporte Hadritech', 'Suporte TOTVS',
  'Otimização de Taxa de Ocupação', 'Parametrização Google Meu Negócio',
  'Campanhas de Marketing Digital', 'Treinamento Contínuo',
];

const Clientes = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    dominio: '',
    plano: '',
    status: 'Ativo',
  });
  const [tab, setTab] = useState(0);
  const [errors, setErrors] = useState({ email: '', cnpj: '', cep: '', telefone: '' });
  const [servicesRaw, setServicesRaw] = useState([]);

  // Serviços e preços vindos da API
  const servicesMap = useMemo(() => {
    const map = {};
    (servicesRaw || []).forEach((it) => {
      const key = it?.nome || '';
      if (!key) return;
      map[key] = Number(it?.valor || 0);
    });
    return map;
  }, [servicesRaw]);

  // Mapa de IDs dos serviços pelo nome, para montar servicos_ids
  const servicesIdMap = useMemo(() => {
    const map = {};
    (servicesRaw || []).forEach((it) => {
      const key = it?.nome || '';
      if (!key) return;
      map[key] = it?.id ?? it?.servico_id ?? it?.codigo ?? null;
    });
    return map;
  }, [servicesRaw]);

  // Habilitação e ações da aba Acessos
  const canOpenPms = () => {
    const svcs = Array.isArray(form?.servicos)
      ? form.servicos
      : (typeof form?.servicos === 'string' && form.servicos
          ? form.servicos.split(',').map((s) => s.trim()).filter(Boolean)
          : []);
    const hasImplantacao = svcs.includes('Implantação Hadritech');
    const required = ['dominio', 'supabase_url', 'db_password'];
    const filled = required.every((k) => String(form?.[k] ?? '').trim().length > 0);
    return hasImplantacao && filled;
  };

  const executarImplantacao = async () => {
    if (!canOpenPms()) return;

    try {
      // Executa a implantação do site e do schema do PMS em paralelo
      const [resSite, resSchema] = await Promise.all([
        fetch(`/api/site/implantar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cliente_id: form?.id }),
        }),
        fetch(`/api/clientes/implantar-schema`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cliente_id: form?.id,
            supabase_url: form?.supabase_url,
            db_password: form?.db_password,
          }),
        }),
      ]);

      const siteOk = resSite.ok;
      const schemaOk = resSchema.ok;

      if (siteOk && schemaOk) {
        alert('Site e PMS implantados com sucesso!');
      } else {
        let errorMessage = 'Ocorreram erros durante a implantação:';
        if (!siteOk) {
          const siteError = await resSite.json().catch(() => ({ message: 'Erro desconhecido ao implantar o site.' }));
          errorMessage += `\n- Site: ${siteError.message}`;
        }
        if (!schemaOk) {
          const schemaError = await resSchema.json().catch(() => ({ message: 'Erro desconhecido ao implantar o PMS.' }));
          errorMessage += `\n- PMS: ${schemaError.message}`;
        }
        throw new Error(errorMessage);
      }
    } catch (e) {
      console.error('Erro ao executar implantação:', e);
      alert(e.message || 'Erro ao executar implantação. Verifique os logs e tente novamente.');
    }
  };

  // Utilitários de formato e cálculo
  const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  const getServicosSelected = useCallback(() => {
    if (!form?.servicos) return [];
    if (Array.isArray(form.servicos)) return form.servicos;
    if (typeof form.servicos === 'string') return form.servicos.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  }, [form?.servicos]);

  const calculateDynamicPrice = useCallback((serviceName, basePrice) => {
    const affectedServices = [
      'Implantação TOTVS PMS',
      'Implantação TOTVS POS',
      'Implantação TOTVS BackSaaS',
    ];

    if (serviceName === 'Suporte TOTVS') {
      const roomCount = Number(form.numero_quartos || 0);
      // Se não houver quartos, o preço do suporte é o base, senão multiplica
      return roomCount > 0 ? basePrice * roomCount : basePrice;
    }

    if (!affectedServices.includes(serviceName)) {
      return basePrice;
    }

    const roomCount = Number(form.numero_quartos || 0);
    if (roomCount <= 30) {
      return basePrice;
    }

    const extraRooms = roomCount - 30;
    const blocksOf10 = Math.ceil(extraRooms / 10);
    const multiplier = 1 + (blocksOf10 * 0.20);
    
    return basePrice * multiplier;
  }, [form.numero_quartos]);

  const { subtotal, total } = useMemo(() => {
    const servicosPrecos = getServicosSelected().map((s) => {
      const basePrice = servicesMap[s] || 0;
      return calculateDynamicPrice(s, basePrice);
    });
    const currentSubtotal = servicosPrecos.reduce((acc, val) => acc + val, 0);
    return { subtotal: currentSubtotal, total: currentSubtotal };
  }, [getServicosSelected, servicesMap, calculateDynamicPrice]);

  const handleOpen = async (data) => {
    setOpenDialog(true);
    setTab(0);
    if (data?.id) {
      try {
        const response = await getCliente(data.id);
        const full = response.data || response;
        // Garante que o status seja 'Ativo' ou 'Inativo'
        const statusNormalized = (full?.status === 'Ativo' || full?.status === 'Inativo') ? full.status : 'Ativo';
        const servicosDoCliente = Array.isArray(full.servicos) ? full.servicos.map(s => s.nome) : [];
        setForm({ ...full, servicos: servicosDoCliente, status: statusNormalized });
      } catch (e) {
        console.warn('Falha ao carregar cliente completo, usando dados parciais.');
        const statusNormalized = (data?.status === 'Ativo' || data?.status === 'Inativo') ? data.status : 'Ativo';
        const formState = { ...data, status: statusNormalized };
        delete formState.ativo;
        setForm(formState);
      }
    } else if (data) {
      const statusNormalized = (data?.status === 'Ativo' || data?.status === 'Inativo') ? data.status : 'Ativo';
      const formState = { ...data, status: statusNormalized };
      delete formState.ativo;
      setForm(formState);
    } else {
      // novo cliente: status padrão 'Ativo' e serviços vazio
      setForm({ status: 'Ativo', servicos: [] });
    }
  };

  // Helpers para seleção de serviços (checkbox)
  const toggleServico = (opt) => {
    setForm((p) => {
      const curr = Array.isArray(p.servicos)
        ? [...p.servicos]
        : (typeof p.servicos === 'string' && p.servicos
            ? p.servicos.split(',').map((s) => s.trim()).filter(Boolean)
            : []);
      const i = curr.indexOf(opt);
      if (i >= 0) curr.splice(i, 1); else curr.push(opt);
      return { ...p, servicos: curr };
    });
  };

  const handleClose = () => {
    setOpenDialog(false);
    setForm({ status: 'Ativo', servicos: [] }); // Limpa o formulário
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await listClientes();
      const data = response.data || response.clientes || response;
      setClientes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar clientes', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async () => {
    try {
      const s = await getClientesSchema();
      if (s && typeof s === 'object') setSchema(s);
    } catch (e) {
      console.warn('Schema de clientes indisponível, usando fallback de campos.');
    }
  };

  useEffect(() => {
    loadClientes();
    loadSchema();
    // carregar serviços para obter preços
    (async () => {
      try {
        const data = await listServicos();
        setServicesRaw(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        console.error('Erro ao carregar serviços', e);
      }
    })();
  }, []);

  useEffect(() => {
    // Regra de negócio: desmarcar "Implantação Hadritech" se quartos >= 30
    if (Number(form.numero_quartos || 0) >= 31) {
      const servicosAtuais = getServicosSelected();
      if (servicosAtuais.includes('Implantação Hadritech')) {
        // Cria um novo array sem o serviço
        const servicosAtualizados = servicosAtuais.filter(s => s !== 'Implantação Hadritech');
        setForm(p => ({ ...p, servicos: servicosAtualizados }));
      }
    }
  }, [form.numero_quartos, getServicosSelected]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const selecionadosArray = getServicosSelected();
      const idsArray = selecionadosArray
        .map((name) => servicesIdMap[name])
        .filter((id) => id !== null && id !== undefined && String(id).trim() !== '');
      
      const payload = { ...form };
      delete payload.ativo;
      if (!payload.id) {
        delete payload.id; // Garante que o ID não seja enviado na criação
      }

      payload.servicos = idsArray;
      payload.plano = form?.plano || 'basico';
      payload.status = form.status === 'Ativo' ? 'Ativo' : 'Inativo';
      payload.numero_quartos = Number(form.numero_quartos || 0);
      payload.valor_total_servicos = total;

      if (form?.id) {
        await updateCliente(form.id, payload);
      } else {
        await createCliente(payload);
      }

      await loadClientes();
      setOpenDialog(false);
    } catch (e) {
      console.error('Erro ao salvar cliente', e);
    } finally {
      setLoading(false);
    }
  };

  // Helper: inferir campos a partir de um objeto (quando schema não está disponível)
  const inferFieldsFromObject = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.keys(obj).map((key) => ({
      name: key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      type: typeof obj[key] === 'number' ? 'number' : typeof obj[key] === 'boolean' ? 'boolean' : 'string',
      readOnly: key.toLowerCase() === 'id',
    }));
  };

  // Campos a renderizar no modal
  const getDialogFields = () => {
    if (schema && Array.isArray(schema.fields)) return schema.fields;
    // usar dados do formulário (cliente selecionado) ou primeiro da lista
    const source = Object.keys(form || {}).length > 0 && (form.id || form.nome || form.dominio) ? form : (clientes[0] || {});
    return inferFieldsFromObject(source);
  };

  const ensureKey = (key, def = '') => {
    if (!(key in form)) {
      setTimeout(() => setForm((p) => ({ ...p, [key]: def })), 0);
    }
    return form[key] ?? def;
  };

  const pad6 = (val) => String(val ?? '').toString().padStart(6, '0');

  // Máscaras e validações
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const formatCNPJ = (v) => {
    const d = onlyDigits(v).slice(0, 14);
    return d
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };
  const formatCEP = (v) => {
    const d = onlyDigits(v).slice(0, 8);
    return d.replace(/(\d{5})(\d)/, '$1-$2');
  };
  const formatTelefone = (v) => {
    const d = onlyDigits(v).slice(0, 11);
    if (d.length <= 10) {
      return d
        .replace(/(\d{0,2})(\d{0,4})(\d{0,4}).*/, (m, a, b, c) => (a ? `(${a}` : '') + (a && a.length === 2 ? ') ' : '') + b + (b && c ? '-' : '') + c);
    }
    return d
      .replace(/(\d{0,2})(\d{0,5})(\d{0,4}).*/, (m, a, b, c) => (a ? `(${a}` : '') + (a && a.length === 2 ? ') ' : '') + b + (b && c ? '-' : '') + c);
  };
  const isEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');

  const handleMaskedChange = (name, value) => {
    let masked = value;
    let err = '';
    if (name === 'documento') {
      masked = formatCNPJ(value);
      if (onlyDigits(masked).length > 0 && onlyDigits(masked).length !== 14) err = 'CNPJ inválido';
    } else if (name === 'cep') {
      masked = formatCEP(value);
      if (onlyDigits(masked).length > 0 && onlyDigits(masked).length !== 8) err = 'CEP inválido';
    } else if (name === 'telefone') {
      masked = formatTelefone(value);
      const d = onlyDigits(masked).length;
      if (d > 0 && d !== 10 && d !== 11) err = 'Telefone inválido';
    } else if (name === 'email') {
      masked = value;
      if (masked && !isEmailValid(masked)) err = 'E-mail inválido';
    }
    setErrors((p) => ({ ...p, [name]: err }));
    setForm((p) => ({ ...p, [name]: masked }));
  };

  const dynamicServices = useMemo(() => {
    if (!servicesRaw?.length) return [];
    const apiServiceNames = servicesRaw.map(s => s.nome);
    const apiServiceNamesSet = new Set(apiServiceNames);

    const orderedServices = SERVICOS_ORDERED.filter(serviceName => apiServiceNamesSet.has(serviceName));
    const additionalServices = apiServiceNames.filter(serviceName => !SERVICOS_ORDERED.includes(serviceName));

    return [...orderedServices, ...additionalServices];
  }, [servicesRaw]);

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" component="h1">Clientes</Typography>
        <Button variant="contained" onClick={() => handleOpen()}>Novo Cliente</Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Buscar cliente" size="small" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Status" size="small" placeholder="Ativo/Inativo" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Plano" size="small" placeholder="Básico/Intermediário/Premium" />
          </Grid>
        </Grid>
        <Box sx={{ width: '100%', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Nome</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Domínio</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Plano</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Data de Criação</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Status</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} style={{ padding: 12 }}>Carregando...</td>
                </tr>
              )}
              {!loading && clientes?.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 12 }}>Nenhum cliente encontrado.</td>
                </tr>
              )}
              {!loading && clientes?.map((c) => (
                <tr key={c.id}>
                  <td style={{ padding: 8 }}>{c.nome}</td>
                  <td style={{ padding: 8 }}>{c.dominio}</td>
                  <td style={{ padding: 8 }}>{c.plano}</td>
                  <td style={{ padding: 8 }}>{c.dataCriacao || '-'}</td>
                  <td style={{ padding: 8 }}>{c.status}</td>
                  <td style={{ padding: 8 }}>
                    <Button size="small" onClick={() => handleOpen(c)}>Editar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>

      {/* Dialog de Cliente */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{form?.id ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
              <Tab label="Dados do Cliente" />
              <Tab label="Serviços" />
              <Tab label="Acessos" />
            </Tabs>
          </Box>

          {tab === 0 && (
            <Grid container spacing={2}>
              {/* Linha 1: ID, Nome Fantasia */}
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="ID" value={pad6(ensureKey('id', ''))} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField fullWidth size="small" label="Nome Fantasia" name="nome_fantasia" value={ensureKey('nome_fantasia', '')} onChange={handleChange} />
              </Grid>

              {/* Linha 2: Email, Telefone */}
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="E-mail" name="email" value={ensureKey('email', '')} onChange={(e) => handleMaskedChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Telefone" name="telefone" value={ensureKey('telefone', '')} onChange={(e) => handleMaskedChange('telefone', e.target.value)} error={!!errors.telefone} helperText={errors.telefone} />
              </Grid>

              {/* Linha 3: Documento, Status */}
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="CNPJ / Documento" name="documento" value={ensureKey('documento', '')} onChange={(e) => handleMaskedChange('documento', e.target.value)} error={!!errors.documento} helperText={errors.documento} />
              </Grid>
              <Grid item xs={12} md={6}>
                 <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  name="status"
                  value={ensureKey('status', 'Ativo')}
                  onChange={handleChange}
                  SelectProps={{ native: true }}
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </TextField>
              </Grid>

              {/* Linha 4: Endereço Completo */}
              <Grid item xs={12} md={12}>
                <TextField fullWidth size="small" label="Endereço" name="endereco" value={ensureKey('endereco', '')} onChange={handleChange} />
              </Grid>

              {/* Linha 5: Cidade, Estado, CEP */}
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" label="Cidade" name="cidade" value={ensureKey('cidade', '')} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Estado" name="estado" value={ensureKey('estado', '')} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="CEP" name="cep" value={ensureKey('cep', '')} onChange={(e) => handleMaskedChange('cep', e.target.value)} error={!!errors.cep} helperText={errors.cep} />
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    label="Número de Quartos"
                    name="numero_quartos"
                    value={ensureKey('numero_quartos', form.qtd_quartos ?? form.num_quartos ?? '')}
                    onChange={(e) => {
                      // Permitir somente números e até 3 dígitos
                      const onlyNums = (e.target.value || '').replace(/\D/g, '').slice(0, 3);
                      setForm((prev) => ({ ...prev, numero_quartos: onlyNums }));
                    }}
                    inputProps={{ maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ width: 200 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Serviços selecionados</Typography>
                <Grid container spacing={1.5}>
                  {dynamicServices.map((servico) => (
                    <Grid key={servico} item xs={12} sm={6} md={4}>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.2, py: 0.8 }}>
                        <FormControlLabel
                          sx={{ m: 0, width: '100%' }}
                          control={(
                            <Checkbox
                              size="small"
                              checked={getServicosSelected().includes(servico)}
                              onChange={() => toggleServico(servico)}
                              disabled={servico === 'Implantação Hadritech' && Number(form.numero_quartos || 0) >= 31}
                            />
                          )}
                          label={<Typography variant="body2">{servico}</Typography>}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Resumo de serviços e valores (somente front) */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Resumo de Serviços e Valores</Typography>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={8} sm={8} md={9}>
                      <Typography variant="caption" color="text.secondary">Serviço</Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={3}>
                      <Typography variant="caption" color="text.secondary" align="right">Valor</Typography>
                    </Grid>
                  </Grid>
                  {getServicosSelected().map((servico) => {
                    const basePrice = servicesMap[servico] || 0;
                    const finalPrice = calculateDynamicPrice(servico, basePrice);
                    return (
                      <Box key={servico} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{servico}</Typography>
                        <Typography variant="body2">
                          {formatCurrency(finalPrice)}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Grid item xs={8} sm={8} md={9}>
                        <Typography variant="body2" fontWeight={600}>Subtotal</Typography>
                      </Grid>
                      <Grid item xs={4} sm={4} md={3}>
                        <Typography variant="body2" align="right">{formatCurrency(subtotal)}</Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={8} sm={8} md={9}>
                        <Typography variant="body1" fontWeight={700}>Total</Typography>
                      </Grid>
                      <Grid item xs={4} sm={4} md={3}>
                        <Typography variant="body1" align="right" fontWeight={700}>{formatCurrency(total)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Domínio" name="dominio" value={ensureKey('dominio', '')} onChange={handleChange} helperText="Ex: pms.nomehotel.com" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Project URL" name="supabase_url" value={ensureKey('supabase_url', '')} onChange={handleChange} helperText="URL do projeto Supabase do cliente" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Senha do Banco de Dados" name="db_password" type="password" value={ensureKey('db_password', '')} onChange={handleChange} helperText="Encontrada em 'Configurações > Banco de dados > Senha' no Supabase" />
              </Grid>

              {/* Divisor para Configurações de E-mail */}
              <Grid item xs={12} sx={{ my: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Configurações de E-mail (SMTP)</Typography>
              </Grid>

              {/* Linha SMTP: Host e Porta */}
              <Grid item xs={12} md={8}>
                <TextField fullWidth size="small" label="SMTP Host" name="smtp_host" value={ensureKey('smtp_host', '')} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="SMTP Porta" name="smtp_port" type="number" value={ensureKey('smtp_port', '')} onChange={handleChange} />
              </Grid>

              {/* Linha SMTP: Usuário e Senha */}
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="SMTP Usuário" name="smtp_user" value={ensureKey('smtp_user', '')} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="SMTP Senha" name="smtp_pass" type="password" value={ensureKey('smtp_pass', '')} onChange={handleChange} />
              </Grid>

              {/* Linha SMTP: Secure */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="SMTP Secure"
                  name="smtp_secure"
                  value={ensureKey('smtp_secure', false)}
                  onChange={(e) => setForm(p => ({ ...p, smtp_secure: e.target.value === 'true' }))}
                  SelectProps={{ native: true }}
                >
                  <option value="true">Sim (TLS/SSL)</option>
                  <option value="false">Não</option>
                </TextField>
              </Grid>
              
              {/* Botão de acesso ao PMS condicionado */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const raw = String(form?.dominio || '').trim();
                      if (!raw) return;
                      const base = raw.startsWith('http') ? raw : `https://${raw}`;
                      let target = base;
                      try {
                        target = new URL('/pms/login', base).href;
                      } catch (e) {
                        // fallback simples caso URL falhe
                        target = `${base.replace(/\/$/, '')}/pms/login`;
                      }
                      window.open(target, '_blank', 'noopener');
                    }}
                    disabled={!form?.dominio}
                  >
                    Abrir PMS
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={executarImplantacao}
                    disabled={!canOpenPms()}
                    title="Implantação Hadritech"
                  >
                    Implantar Site + PMS
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Se não houver schema e nenhum campo acima fez sentido, renderiza dinâmico como fallback */}
          {(!schema || !schema.fields) && tab === 0 && Object.keys(form || {}).length <= 4 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {getDialogFields().map((field) => {
                const key = field.name || field.key;
                if (!key) return null;
                const label = field.label || key.charAt(0).toUpperCase() + key.slice(1);
                const type = (field.type || 'string').toLowerCase();
                const value = form[key] ?? '';

                if (!(key in form)) {
                  setTimeout(() => setForm((prev) => ({ ...prev, [key]: value })), 0);
                }

                if (type === 'number' || type === 'integer') {
                  return (
                    <Grid key={key} item xs={12} md={4}>
                      <TextField fullWidth size="small" type="number" label={label} name={key} value={value} onChange={handleChange} />
                    </Grid>
                  );
                }
                return (
                  <Grid key={key} item xs={12} md={6}>
                    <TextField fullWidth size="small" label={label} name={key} value={value} onChange={handleChange} />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Clientes;
